import { z } from "zod";

// ── Sub-schemas ──

export const GreetingStyleEntrySchema = z.object({
	channel: z.enum(["linkedin", "email"]),
	messageType: z.string(),
	greeting: z.string(),
});

export const AlternativePhraseSchema = z.object({
	original: z.string(),
	replacement: z.string(),
});

export const MessageLimitEntrySchema = z.object({
	messageType: z.string(),
	maxChars: z.number().nullable().optional(),
	minWords: z.number().nullable().optional(),
	maxWords: z.number().nullable().optional(),
});

export const SendDelayEntrySchema = z.object({
	step: z.number(),
	delayDays: z.number(),
});

// ── Response schema ──

export const CopywritingSettingsSchema = z.object({
	id: z.number(),
	companyId: z.number(),
	greetingStyle: z.array(GreetingStyleEntrySchema).nullable(),
	signOffStyle: z.string().nullable(),
	wordingBlacklist: z.array(z.string()).nullable(),
	alternativePhrases: z.array(AlternativePhraseSchema).nullable(),
	messageLimits: z.array(MessageLimitEntrySchema).nullable(),
	ctaPool: z.array(z.string()).nullable(),
	subjectLinePatterns: z.string().nullable(),
	sendDelayDefaults: z.array(SendDelayEntrySchema).nullable(),
	/** Campaign emails get the mailbox signature; new email drafts start with it on (PREC-4401). */
	appendEmailSignature: z.boolean().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

/** `GET copywriting-settings/send-delay-defaults`: what the campaign builders read (PREC-4299). */
export const SendDelayDefaultsSchema = z.object({
	sendDelayDefaults: z.array(SendDelayEntrySchema).nullable(),
});

/** `GET copywriting-settings/email-signature-default`: the composer's Include signature default. */
export const EmailSignatureDefaultSchema = z.object({
	appendEmailSignature: z.boolean(),
});

// ── Upsert body schema (for form validation) ──

export const UpsertCopywritingSettingsSchema = z.object({
	greetingStyle: z.array(GreetingStyleEntrySchema).optional(),
	signOffStyle: z.string().optional(),
	wordingBlacklist: z.array(z.string()).optional(),
	alternativePhrases: z.array(AlternativePhraseSchema).optional(),
	messageLimits: z.array(MessageLimitEntrySchema).optional(),
	ctaPool: z.array(z.string()).optional(),
	subjectLinePatterns: z.string().optional(),
	sendDelayDefaults: z.array(SendDelayEntrySchema).optional(),
	appendEmailSignature: z.boolean().optional(),
});

// ── Types ──

export type GreetingStyleEntry = z.infer<typeof GreetingStyleEntrySchema>;
export type AlternativePhrase = z.infer<typeof AlternativePhraseSchema>;
export type MessageLimitEntry = z.infer<typeof MessageLimitEntrySchema>;
export type SendDelayEntry = z.infer<typeof SendDelayEntrySchema>;
export type SendDelayDefaults = z.infer<typeof SendDelayDefaultsSchema>;
export type EmailSignatureDefault = z.infer<typeof EmailSignatureDefaultSchema>;
export type CopywritingSettings = z.infer<typeof CopywritingSettingsSchema>;
export type UpsertCopywritingSettings = z.infer<typeof UpsertCopywritingSettingsSchema>;
